import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Badge, Button, Col, Row, UncontrolledTooltip } from "reactstrap"

import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import TableContainer from "components/Common/TableContainer"
import { useListParams } from "hooks/useListParams"
import { useDeleteReminder, useReminders } from "hooks/reminderHook"
import ReminderAddEdit from "./ReminderAddEdit"

const scheduleSummary = row => {
  const times = (row.times ?? []).join(", ")
  if (row.frequency === "weekly") return `${(row.days_of_week ?? []).join(", ")} @ ${times}`
  if (row.frequency === "monthly") return `Day ${row.day_of_month} @ ${times}`
  return `Daily @ ${times}`
}

const targetSummary = row => {
  if (row.target_type === "permission") return row.target_permission
  if (row.target_type === "users") return `${row.target_user_ids?.length ?? 0} user(s)`
  return "Everyone"
}

const ReminderList = () => {
  document.title = "Reminders"

  const { params, setPage, setSearch } = useListParams({ page: 1, search: "" })
  const [modal, setModal] = useState({ open: false, reminder: null })
  const [deleteTarget, setDeleteTarget] = useState(null)

  const queryParams = useMemo(
    () => ({ page: params.page ?? 1, per_page: 20, search: params.search }),
    [params]
  )

  const { data: resp, isLoading, isRefetching, refetch } = useReminders(queryParams)
  const rows = resp?.data?.data ?? []
  const paginationInfo = resp?.data

  const deleteMutation = useDeleteReminder()

  const openCreate = () => setModal({ open: true, reminder: null })
  const openEdit = reminder => setModal({ open: true, reminder })
  const closeModal = () => {
    setModal({ open: false, reminder: null })
    refetch()
  }

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(deleteTarget.id)
      toast.success("Reminder deleted")
      setDeleteTarget(null)
      refetch()
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed")
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "Title",
        accessorKey: "title_en",
        cell: ({ row }) => (
          <div>
            <div className="fw-medium">{row.original.title_en}</div>
            {row.original.title_bn && (
              <div className="fw-medium" lang="bn">
                {row.original.title_bn}
              </div>
            )}
            <small className="text-muted d-block">{row.original.body_en}</small>
            {row.original.body_bn && (
              <small className="text-muted d-block" lang="bn">
                {row.original.body_bn}
              </small>
            )}
          </div>
        ),
      },
      {
        header: "Schedule",
        cell: ({ row }) => (
          <div>
            <Badge color="light" className="text-dark text-capitalize">
              {row.original.frequency}
            </Badge>{" "}
            <small>{scheduleSummary(row.original)}</small>
          </div>
        ),
      },
      { header: "Target", cell: ({ row }) => targetSummary(row.original) },
      {
        header: "Showroom",
        cell: ({ row }) => row.original.showroom_id ?? "All",
      },
      {
        header: "Status",
        cell: ({ row }) => (
          <div className="d-flex flex-column gap-1">
            <Badge color={row.original.active ? "success" : "secondary"}>
              {row.original.active ? "Active" : "Inactive"}
            </Badge>
            {row.original.skip_friday && (
              <Badge color="warning">Skips Friday</Badge>
            )}
          </div>
        ),
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <div className="d-flex gap-1">
            <Button size="sm" color="primary" onClick={() => openEdit(row.original)} id={`edit-${row.original.id}`}>
              <i className="mdi mdi-pencil-outline" />
            </Button>
            <UncontrolledTooltip target={`edit-${row.original.id}`}>Edit</UncontrolledTooltip>
            <Button size="sm" color="danger" onClick={() => setDeleteTarget(row.original)} id={`del-${row.original.id}`}>
              <i className="mdi mdi-delete-outline" />
            </Button>
            <UncontrolledTooltip target={`del-${row.original.id}`}>Delete</UncontrolledTooltip>
          </div>
        ),
      },
    ],
    []
  )

  return (
    <PageWrapper title="Reminders" breadcrumbItem="Reminders">
      <Row className="mb-3 align-items-center">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} placeholder="Search reminders..." />
        </Col>
        <Col className="text-end">
          <Button color="primary" onClick={openCreate}>
            <i className="mdi mdi-plus" /> Add Reminder
          </Button>
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={rows}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        className="table-responsive"
      />

      {modal.open && (
        <ReminderAddEdit isOpen={modal.open} toggle={closeModal} reminder={modal.reminder} />
      )}

      <DeleteModal
        show={Boolean(deleteTarget)}
        loading={deleteMutation.isPending}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteTarget(null)}
      />
    </PageWrapper>
  )
}

export default ReminderList
